import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Circle, RocketLaunch } from "@phosphor-icons/react";
import api from "../api";
import { useAuth } from "../AuthContext";

/**
 * v406.9 — "Getting Started" checklist on the distributor dashboard.
 * Auto-detected from real data (mirrors the walkthrough email); each undone
 * step deep-links to the action. Hides itself once everything is complete.
 */
const STEP_LINKS = {
  price: "/my-store",
  sale: null, // done from a product card's Mark Sold
  stock: null, // arrives from HQ
  order: null, // customers place these
  login: null,
};

export const GettingStartedCard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/onboarding/progress").then(({ data }) => setProgress(data)).catch(() => {});
  }, []);

  if (!progress || progress.complete) return null;
  const pct = Math.round((progress.done / progress.total) * 100);

  return (
    <div className="bg-white border border-[#c9a24a]/60 rounded-2xl p-5" data-testid="getting-started-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <RocketLaunch size={18} weight="bold" className="text-[#8a6a1c]" />
          {t("getting_started.title")}
        </h2>
        <span className="text-xs font-semibold text-[#8a6a1c]" data-testid="getting-started-count">
          {t("getting_started.progress", { done: progress.done, total: progress.total })}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full mt-3 overflow-hidden">
        <div className="h-full bg-[#c9a24a] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-4 space-y-2.5">
        {progress.steps.map((s) => (
          <li key={s.key} className="flex items-start gap-2.5" data-testid={`gs-step-${s.key}`}>
            {s.done ? (
              <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
            ) : (
              <Circle size={18} className="text-zinc-300 shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <p className={`text-sm font-medium ${s.done ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
                {t(`getting_started.step_${s.key}`)}
              </p>
              {!s.done && (
                <p className="text-xs text-zinc-500">
                  {t(`getting_started.hint_${s.key}`, { code: user?.distributor_code || "" })}
                  {STEP_LINKS[s.key] && (
                    <>
                      {" "}
                      <Link to={STEP_LINKS[s.key]} className="text-[#8a6a1c] font-semibold hover:underline"
                        data-testid={`gs-link-${s.key}`}>
                        {t("getting_started.go")}
                      </Link>
                    </>
                  )}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
